import React, { useState } from "react";
import * as XLSX from "xlsx";
import { ArrowLeft, Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import hsnApi from "../../api/hsnApi";
import { useSelector } from "react-redux";

const BulkHsnUpload = ({ onComplete, onCancel }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();
    const token = useSelector((state) => state.auth.access_token);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  };

  const handleFile = (file) => {
    const allowedTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
    ];
    if (!allowedTypes.includes(file.type) && !file.name.endsWith(".csv")) {
      setValidationErrors(["Please upload a valid Excel (.xlsx, .xls) or CSV file"]);
      return;
    }
    setUploadedFile(file);
    setValidationErrors([]);
  };

  const downloadHsnTemplate = () => {
    const templateData = [
      {
        hsnCode4: 9983,
        hsnCode8: "99831300",
        productGroupName: "Professional Consultation Service",
        gstRate: 0.18
      },
      {
        hsnCode4: 3004,
        hsnCode8: "30041010",
        productGroupName: "Medicines for Retail Sale",
        gstRate: 0.12
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "HSN_Template");
    XLSX.writeFile(wb, "HSN_Master_Template.xlsx");
    toast.info("Template downloaded successfully");
  };

  const processUpload = async () => {
    if (!uploadedFile) {
      toast.error("⚠️ Please select a file first.");
      return;
    }

    setUploading(true);
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        const formattedHsnData = jsonData.map((item, index) => {
          const errors = [];
          if (!item.hsnCode4) errors.push(`Row ${index + 1}: Missing 4-digit HSN`);
          if (!item.productGroupName) errors.push(`Row ${index + 1}: Missing Group Name`);
          
          return {
            hsnCode4: parseInt(item.hsnCode4),
            hsnCode8: item.hsnCode8?.toString() || "",
            productGroupName: item.productGroupName || "",
            gstRate: parseFloat(item.gstRate) || 0,
            isValid: errors.length === 0,
            errors
          };
        });

        const errorsList = formattedHsnData.flatMap(item => item.errors);
        if (errorsList.length > 0) {
          setValidationErrors(errorsList.slice(0, 5)); // Show first 5 errors
          setUploading(false);
          return;
        }

        console.log("Sending to API:", formattedHsnData);
         await hsnApi.uploadHsn(formattedHsnData,token)

        toast.success(`Successfully processed ${formattedHsnData.length} HSN codes`);
        if (onComplete) onComplete(formattedHsnData);
      } catch (err) {
        toast.error("Error parsing file. Ensure it matches the template.");
      } finally {
        setUploading(false);
      }
    };

    reader.readAsArrayBuffer(uploadedFile);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-4 mb-2">
        <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-full">
          <ArrowLeft className="w-6 h-6 text-gray-600" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">HSN Master Bulk Upload</h1>
      </div>

      <div className="bg-amber-50 border border-amber-200 px-4 py-1 rounded-lg flex gap-3 mt-3">
        <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
        <p className="text-sm text-amber-800">
          <strong>Note:</strong> Ensure <strong>gstRate</strong> is in decimal (e.g., 0.18 for 18%). 
          HSN 4-digit is mandatory for Indian GST compliance.
        </p>
      </div>

      <div className="bg-white px-6 py-1 rounded-xl border border-gray-200 shadow-sm flex justify-between items-center">
        <div>
          <h3 className="font-semibold">1. Download HSN Structure</h3>
          <p className="text-sm text-gray-500">Get the required Excel format for HSN/SAC codes.</p>
        </div>
        <button 
          onClick={downloadHsnTemplate}
          className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition"
        >
          <Download size={18} /> Template
        </button>
      </div>

      <div 
        onDragEnter={handleDrag} onDragOver={handleDrag} onDragLeave={handleDrag} onDrop={handleDrop}
        className={`border-2 border-dashed rounded-2xl p-12 text-center transition ${
          dragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 bg-gray-50"
        }`}
      >
        <input 
          type="file" accept=".xlsx,.xls,.csv" 
          className="hidden" id="hsn-upload"
          onChange={(e) => handleFile(e.target.files[0])}
        />
        <label htmlFor="hsn-upload" className="cursor-pointer">
          <FileSpreadsheet className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-700">
            {uploadedFile ? uploadedFile.name : "Click to upload or drag & drop"}
          </p>
          <p className="text-sm text-gray-500 mt-1">Excel or CSV files only</p>
        </label>
      </div>

      {validationErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
          <h4 className="text-red-800 font-bold mb-2 flex items-center gap-2">
            <AlertCircle size={16} /> Validation Failed
          </h4>
          <ul className="text-sm text-red-700 list-disc ml-5">
            {validationErrors.map((err, i) => <li key={i}>{err}</li>)}
          </ul>
        </div>
      )}

      <div className="flex justify-end gap-4 pt-4">
        <button onClick={onCancel} className="px-6 py-2 text-gray-600 font-medium">Cancel</button>
        <button 
          onClick={processUpload}
          disabled={!uploadedFile || uploading}
          className="bg-blue-600 text-white px-8 py-2 rounded-lg font-bold disabled:opacity-50 flex items-center gap-2"
        >
          {uploading ? "Processing..." : <><Upload size={18} /> Start Import</>}
        </button>
      </div>
    </div>
  );
};

export default BulkHsnUpload;